import 'mapbox-gl/dist/mapbox-gl.css';
import { ActivityIndicator, Button, Dialog, DialogContent, Label, PillButton } from '@troon/ui';
import { Title } from '@solidjs/meta';
import { createEffect, createRenderEffect, createSignal, For, Suspense, Switch, Match, Show } from 'solid-js';
import { createAsync, useNavigate } from '@solidjs/router';
import { WindowVirtualizer } from 'virtua/solid';
import { createIntersectionObserver } from '@solid-primitives/intersection-observer';
import { IconArrowUpMd } from '@troon/icons/arrow-up-md';
import { isServer } from 'solid-js/web';
import { useWindowScrollPosition } from '@solid-primitives/scroll';
import { createSearchStore } from '@troon/primitives/search-store';
import { cachedGet } from '@troon/api-client';
import { produce } from 'solid-js/store';
import { IconFilter } from '@troon/icons/filter';
import { IconMap } from '@troon/icons/map';
import { IconShrink } from '@troon/icons/shrink';
import { twJoin } from '@troon/tailwind-preset/merge';
import { SearchLocations } from '../../components/search-locations';
import { Map } from '../../components/map/map';
import { FacilityCard } from '../../components/facility-search/card';
import { schema } from '../../components/facility-search/schema';
import { FacilitySearchBlankData } from '../../components/facility-search/blank-data';
import { ZoomControls } from '../../components/map/zoom';
import { SearchBoundsButton } from '../../components/map/search-bounds';
import { getPositionClass } from '../../components/map/context';
import type { ApiResponse } from '@troon/api-client';
import type { RouteDefinition } from '@solidjs/router';
import type { MapHandler } from '../../components/map/map';
import type { SearchStore, SetSearchStore } from '@troon/primitives/search-store';
import type { WindowVirtualizerHandle } from 'virtua/solid';
import type { z } from 'zod';

const SCROLL_OFFSET = -155; // magic number: ~ 1rem * 10 to account for header

const sessionKeyPrefix = 'fa-scroll-index';

type Facility = ApiResponse<'get', '/v0/facilities'>['facilities'][number];

export default function FacilitySearch() {
	const [search, setSearch] = createSearchStore(schema);
	const [virtualizer, setVirtualizer] = createSignal<WindowVirtualizerHandle>();
	const [mapRef, setMapRef] = createSignal<MapHandler>();
	const [targets, setTargets] = createSignal<Array<HTMLElement>>([]);
	const [mapExpanded, setMapExpanded] = createSignal(false);
	const [focused, setFocused] = createSignal<Facility>();
	const [highlighted, setHighlighted] = createSignal<Array<Element>>([]);
	const [showFilters, setShowFilters] = createSignal(false);
	const scroll = useWindowScrollPosition();
	const navigate = useNavigate();

	createIntersectionObserver(
		targets,
		(entries) => {
			if (window.scrollY < Math.abs(SCROLL_OFFSET)) {
				return;
			}
			setHighlighted((s) => {
				for (const el of entries) {
					const idx = s.indexOf(el.target);
					if (!el.isIntersecting && idx > -1) {
						s.splice(idx, 1);
					}
					if (el.isIntersecting && idx === -1) {
						s.push(el.target);
					}
				}
				return [...s];
			});
		},
		{ threshold: 0.7, rootMargin: '-50px 0px -25% 0px' },
	);

	createEffect(() => {
		const els = highlighted();
		const handler = mapRef();
		if (handler) {
			if (els.length) {
				const slug = els[0]!.getAttribute('data-id');
				const facility = data()?.facilities.find((f) => f.slug === slug);

				if (facility && handler) {
					handler.setFocus(facility);
				}
			}
		}
	});

	createEffect(() => {
		if (mapExpanded()) {
			setFocused();
			setHighlighted([]);
		}
	});

	const data = createAsync(
		async () => {
			const query = {
				isBookable: true,
				regionIds: search.regionId ? [search.regionId] : [],
				location:
					search.lat && search.lon ? { latitude: search.lat, longitude: search.lon, radiusMiles: search.radius } : {},
				supportsAccessPoints: search.accessPoints ? true : undefined,
				supportsTroonAccess: search.premiumRates ? true : undefined,
			} satisfies Parameters<typeof getFacilities>[0]['query'];

			if (!query.regionIds && !query.location) {
				return null;
			}
			const data = await getFacilities({ query });
			setFocused();
			setHighlighted([]);
			return data;
		},
		{ deferStream: true },
	);

	createEffect(() => {
		if (data() && !isServer) {
			window.scrollTo({ top: 0 });
		}
	});

	createRenderEffect(() => {
		try {
			const scrollY = parseInt(sessionStorage.getItem(`${sessionKeyPrefix}-${location.pathname}`) ?? '');
			if (data.latest?.totalCount && virtualizer() && !isNaN(scrollY)) {
				virtualizer()?.scrollToIndex(scrollY, { offset: SCROLL_OFFSET });
				sessionStorage.removeItem(`${sessionKeyPrefix}-${location.pathname}`);
			}
		} catch {
			// ignore browsers disallowing session access
		}
	});

	return (
		<>
			<Title>Find golf courses | Golf with Access</Title>
			<div class="-mb-12 flex gap-4">
				<div class="w-full max-w-svw px-4 lg:max-w-lg lg:min-w-md lg:grow lg:pe-0">
					<div class="sticky top-16 z-40 flex flex-col gap-4 bg-white py-4">
						<div class="flex gap-4 overflow-hidden">
							<SearchLocations
								onSelectFacility={(facility) => navigate(`/course/${facility.slug}`)}
								onSelectPlace={(place) => {
									setSearch({
										query: place.displayValue,
										regionId: null,
										lat: place.coordinates.latitude,
										lon: place.coordinates.longitude,
									});
								}}
								onSelectRegion={(region) => {
									setSearch({ query: region.displayValue, lat: null, lon: null, regionId: region.id });
								}}
								defaultValue={search.query as string}
								inputClass="ps-10 py-4"
								mobileInputClass="ps-3 border border-neutral py-4 shrink"
								placeholder="Search by city, state, or course name"
							/>
							<Button
								aria-pressed={showFilters()}
								appearance="tertiary"
								onClick={() => setShowFilters((s) => !s)}
								class="aspect-square grow-0 aria-pressed:bg-brand-500 aria-pressed:text-white"
							>
								<IconFilter />
								<span class="sr-only">Filter results</span>
							</Button>
						</div>

						<Show when={showFilters()}>
							<SearchFilters filters={search} setFilters={setSearch} />
						</Show>
					</div>

					<Suspense
						fallback={
							<div class="min-h-dvh">
								<ActivityIndicator />
							</div>
						}
					>
						<Show
							when={data()?.facilities.length}
							fallback={<FacilitySearchBlankData filters={search} setFilters={setSearch} />}
						>
							{(count) => (
								<>
									<div
										class="pointer-events-none sticky inset-x-auto top-28 z-30 -mb-12 grid place-content-center transition-all delay-1000 duration-300 lg:top-36"
										classList={{
											'-translate-y-32': scroll.y <= Math.abs(SCROLL_OFFSET),
											'translate-y-4': scroll.y > Math.abs(SCROLL_OFFSET) && !showFilters(),
											'translate-y-20': scroll.y > Math.abs(SCROLL_OFFSET) && showFilters(),
										}}
									>
										<Button
											appearance="tertiary"
											size="sm"
											class="pointer-events-auto shadow-lg"
											onClick={() => {
												window.scrollTo({ top: 0, behavior: 'smooth' });
											}}
										>
											<IconArrowUpMd />
											Back to top
										</Button>
									</div>
									<p class="flex h-16 items-center font-medium text-neutral-700">Showing {count()} locations</p>
									<WindowVirtualizer
										bufferSize={500}
										ref={setVirtualizer}
										data={data.latest?.facilities || []}
										itemSize={600}
									>
										{(facility, index) => (
											<div
												ref={(el) => setTargets((t) => [...t, el])}
												data-id={facility.slug}
												class="pb-16"
												onClick={() => {
													sessionStorage.setItem(`${sessionKeyPrefix}-${location.pathname}`, index().toString());
												}}
												onMouseEnter={() => mapRef()?.setFocus(facility)}
												onMouseLeave={() => mapRef()?.setFocus()}
											>
												<FacilityCard facility={facility} />
											</div>
										)}
									</WindowVirtualizer>
								</>
							)}
						</Show>
					</Suspense>
					<div class="sticky bottom-4 flex justify-center lg:hidden" classList={{ hidden: mapExpanded() }}>
						<Button class="size-fit grow-0" onClick={() => setMapExpanded(true)}>
							<IconMap />
							View Map
						</Button>
					</div>
				</div>
				<div
					class="top-16 z-50 lg:sticky lg:my-4 lg:me-4 lg:block lg:h-[calc(100dvh-6rem)] lg:grow lg:overflow-hidden lg:rounded-lg"
					classList={{
						'fixed inset-x-0 bottom-0 block': mapExpanded(),
						'top-38': mapExpanded() && !showFilters(),
						'top-52': mapExpanded() && showFilters(),
						hidden: !mapExpanded(),
					}}
				>
					<Suspense>
						<Map
							ref={setMapRef}
							facilities={(data.latest?.facilities ?? []).reduce(
								(memo, facility) => {
									if (facility.supportsAccessPoints && facility.supportsTroonAccess) {
										memo[pins.both].push(facility);
									} else if (facility.supportsAccessPoints || facility.supportsTroonAccess) {
										memo[pins.points].push(facility);
									} else {
										memo[pins.none].push(facility);
									}
									return memo;
								},
								{
									'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-light.svg)]': [],
									'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-brand.svg)]': [],
									'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-dark.svg)]': [],
								} as Record<(typeof pins)[keyof typeof pins], Array<Facility>>,
							)}
							latitude={search.lat}
							longitude={search.lon}
							onExpand={(expanded) => {
								setMapExpanded(expanded);
								if (expanded) {
									window.scrollTo({ top: 0, behavior: 'smooth' });
								}
							}}
							onSelectFacility={(facility) => {
								const index = data()?.facilities.findIndex((f) => f.slug === facility.slug);
								virtualizer()?.scrollToIndex(index ?? 0, {
									smooth: true,
									offset: SCROLL_OFFSET,
								});
								const handler = mapRef();
								if (typeof index !== 'undefined' && index > -1 && facility && handler) {
									setFocused(data()!.facilities[index]);
									!mapExpanded() && handler.setFocus(facility);
								}
							}}
							onSearchBounds={(center, dist) => {
								setSearch({ regionId: null, lat: center.lat, lon: center.lng, radius: dist, query: null });
							}}
						>
							<SearchBoundsButton position="top" />
							<ZoomControls position="bottom-right" />
							<Show when={mapExpanded()}>
								<Button
									appearance="tertiary"
									size="sm"
									class={twJoin('size-fit grow-0', getPositionClass('bottom-left'))}
									onClick={() => setMapExpanded(false)}
								>
									<IconShrink />
									Hide map
								</Button>
							</Show>
						</Map>
					</Suspense>

					<Switch>
						<Match when={mapExpanded() && focused()}>
							{(focused) => (
								<Dialog
									open
									key="course-search"
									onOpenChange={(open) => {
										if (!open) {
											setFocused(undefined);
										}
									}}
								>
									<DialogContent height="fit" header="Course details" headerLevel="h2">
										<FacilityCard facility={focused()} />
									</DialogContent>
								</Dialog>
							)}
						</Match>
					</Switch>
				</div>
			</div>
		</>
	);
}

const getFacilities = cachedGet('/v0/facilities');

type SearchFiltersProps = {
	filters: SearchStore<typeof schema>;
	setFilters: SetSearchStore<typeof schema>;
};

function SearchFilters(props: SearchFiltersProps) {
	return (
		<div role="group" class="relative z-10 flex gap-2 bg-white">
			<Label class="sr-only">Filters</Label>
			<For each={Object.keys(programs) as Array<keyof typeof programs>}>
				{(key) => (
					<PillButton
						value={key}
						aria-pressed={!!props.filters[key as keyof z.infer<typeof schema>]}
						onClick={() => {
							props.setFilters(
								produce((s) => {
									s[key] = !s[key];
								}),
							);
						}}
					>
						{programs[key]}
					</PillButton>
				)}
			</For>
		</div>
	);
}

const pins = {
	none: 'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-light.svg)]',
	points: 'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-brand.svg)]',
	both: 'bg-[url(https://cdn.golfwithaccess.com/web/photo/icons/map-pin-dark.svg)]',
} as const;

const programs: Record<'accessPoints' | 'premiumRates', string> = {
	accessPoints: 'Access Points',
	premiumRates: 'Premium Rates',
};

export const route = { info: { nav: { sticky: true }, banner: { hide: true } } } satisfies RouteDefinition;
